import { Injectable } from '@angular/core';
import { trigger, transition, style, animate, keyframes, query, stagger } from '@angular/animations';

export interface AnimationConfig {
  duration: number;
  delay?: number;
  easing?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnimationService {
  
  // Animações predefinidas
  static readonly animations = {
    // Fade in/out
    fadeIn: trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ opacity: 0 }))
      ])
    ]),

    // Slide in/out
    slideIn: trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)', opacity: 0 }),
        animate('300ms ease-in', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ transform: 'translateX(100%)', opacity: 0 }))
      ])
    ]),

    // Scale in/out
    scaleIn: trigger('scaleIn', [
      transition(':enter', [
        style({ transform: 'scale(0)', opacity: 0 }),
        animate('300ms ease-in', style({ transform: 'scale(1)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ transform: 'scale(0)', opacity: 0 }))
      ])
    ]),

    // Bounce in
    bounceIn: trigger('bounceIn', [
      transition(':enter', [
        animate('600ms ease-in', keyframes([
          style({ transform: 'scale(0)', offset: 0 }),
          style({ transform: 'scale(1.2)', offset: 0.6 }),
          style({ transform: 'scale(1)', offset: 1 })
        ]))
      ])
    ]),

    // Stagger animation para listas
    staggerIn: trigger('staggerIn', [
      transition(':enter', [
        query(':enter', [
          style({ opacity: 0, transform: 'translateY(20px)' }),
          stagger(100, [
            animate('300ms ease-in', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ])
      ])
    ]),

    // Pulse animation
    pulse: trigger('pulse', [
      transition(':enter', [
        animate('1000ms ease-in-out', keyframes([
          style({ transform: 'scale(1)', offset: 0 }),
          style({ transform: 'scale(1.05)', offset: 0.5 }),
          style({ transform: 'scale(1)', offset: 1 })
        ]))
      ])
    ]),

    // Shake animation
    shake: trigger('shake', [
      transition(':enter', [
        animate('500ms ease-in-out', keyframes([
          style({ transform: 'translateX(0)', offset: 0 }),
          style({ transform: 'translateX(-10px)', offset: 0.1 }),
          style({ transform: 'translateX(10px)', offset: 0.2 }),
          style({ transform: 'translateX(-10px)', offset: 0.3 }),
          style({ transform: 'translateX(10px)', offset: 0.4 }),
          style({ transform: 'translateX(0)', offset: 1 })
        ]))
      ])
    ]),

    // Rotate animation
    rotate: trigger('rotate', [
      transition(':enter', [
        style({ transform: 'rotate(0deg)' }),
        animate('300ms ease-in', style({ transform: 'rotate(360deg)' }))
      ])
    ]),

    // Slide up
    slideUp: trigger('slideUp', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate('300ms ease-in', style({ transform: 'translateY(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ transform: 'translateY(100%)', opacity: 0 }))
      ])
    ]),

    // Slide down
    slideDown: trigger('slideDown', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate('300ms ease-in', style({ transform: 'translateY(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ transform: 'translateY(-100%)', opacity: 0 }))
      ])
    ])
  };

  /**
   * Cria uma animação customizada
   */
  createCustomAnimation(
    name: string,
    enterAnimation: any,
    leaveAnimation?: any
  ) {
    const transitions = [':enter', enterAnimation];
    if (leaveAnimation) {
      transitions.push(':leave', leaveAnimation);
    }

    return trigger(name, transitions as any);
  }

  /**
   * Cria animação de fade com configuração customizada
   */
  createFadeAnimation(config: AnimationConfig) {
    return trigger('customFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate(`${config.duration}ms ${config.easing || 'ease-in'}`, 
          style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate(`${config.duration}ms ${config.easing || 'ease-out'}`, 
          style({ opacity: 0 }))
      ])
    ]);
  }

  /**
   * Cria animação de slide com configuração customizada
   */
  createSlideAnimation(config: AnimationConfig, direction: 'left' | 'right' | 'up' | 'down') {
    const slideStyles = {
      left: { transform: 'translateX(-100%)', opacity: 0 },
      right: { transform: 'translateX(100%)', opacity: 0 },
      up: { transform: 'translateY(-100%)', opacity: 0 },
      down: { transform: 'translateY(100%)', opacity: 0 }
    };

    const enterStyle = slideStyles[direction];
    const leaveStyle = slideStyles[direction];

    return trigger('customSlide', [
      transition(':enter', [
        style(enterStyle),
        animate(`${config.duration}ms ${config.easing || 'ease-in'}`, 
          style({ transform: 'translate(0, 0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate(`${config.duration}ms ${config.easing || 'ease-out'}`, 
          style(leaveStyle))
      ])
    ]);
  }

  /**
   * Cria animação de scale com configuração customizada
   */
  createScaleAnimation(config: AnimationConfig) {
    return trigger('customScale', [
      transition(':enter', [
        style({ transform: 'scale(0)', opacity: 0 }),
        animate(`${config.duration}ms ${config.easing || 'ease-in'}`, 
          style({ transform: 'scale(1)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate(`${config.duration}ms ${config.easing || 'ease-out'}`, 
          style({ transform: 'scale(0)', opacity: 0 }))
      ])
    ]);
  }

  /**
   * Cria animação de stagger para listas
   */
  createStaggerAnimation(
    staggerDelay: number = 100,
    itemAnimation: any = 'fadeIn'
  ) {
    return trigger('customStagger', [
      transition(':enter', [
        query(':enter', [
          style({ opacity: 0, transform: 'translateY(20px)' }),
          stagger(staggerDelay, [
            animate('300ms ease-in', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ])
      ])
    ]);
  }

  /**
   * Cria animação de bounce customizada
   */
  createBounceAnimation(config: AnimationConfig) {
    return trigger('customBounce', [
      transition(':enter', [
        animate(`${config.duration}ms ${config.easing || 'ease-in'}`, keyframes([
          style({ transform: 'scale(0)', offset: 0 }),
          style({ transform: 'scale(1.2)', offset: 0.6 }),
          style({ transform: 'scale(1)', offset: 1 })
        ]))
      ])
    ]);
  }

  /**
   * Cria animação de shake customizada
   */
  createShakeAnimation(config: AnimationConfig) {
    return trigger('customShake', [
      transition(':enter', [
        animate(`${config.duration}ms ${config.easing || 'ease-in-out'}`, keyframes([
          style({ transform: 'translateX(0)', offset: 0 }),
          style({ transform: 'translateX(-10px)', offset: 0.1 }),
          style({ transform: 'translateX(10px)', offset: 0.2 }),
          style({ transform: 'translateX(-10px)', offset: 0.3 }),
          style({ transform: 'translateX(10px)', offset: 0.4 }),
          style({ transform: 'translateX(0)', offset: 1 })
        ]))
      ])
    ]);
  }

  /**
   * Cria animação de pulse customizada
   */
  createPulseAnimation(config: AnimationConfig) {
    return trigger('customPulse', [
      transition(':enter', [
        animate(`${config.duration}ms ${config.easing || 'ease-in-out'}`, keyframes([
          style({ transform: 'scale(1)', offset: 0 }),
          style({ transform: 'scale(1.05)', offset: 0.5 }),
          style({ transform: 'scale(1)', offset: 1 })
        ]))
      ])
    ]);
  }

  /**
   * Cria animação de rotação customizada
   */
  createRotateAnimation(config: AnimationConfig, degrees: number = 360) {
    return trigger('customRotate', [
      transition(':enter', [
        style({ transform: 'rotate(0deg)' }),
        animate(`${config.duration}ms ${config.easing || 'ease-in'}`, 
          style({ transform: `rotate(${degrees}deg)` }))
      ])
    ]);
  }

  /**
   * Cria animação de loading spinner
   */
  createLoadingAnimation(config: AnimationConfig) {
    return trigger('customLoading', [
      transition(':enter', [
        animate(`${config.duration}ms linear infinite`, keyframes([
          style({ transform: 'rotate(0deg)', offset: 0 }),
          style({ transform: 'rotate(360deg)', offset: 1 })
        ]))
      ])
    ]);
  }

  /**
   * Cria animação de progress bar
   */
  createProgressAnimation(config: AnimationConfig) {
    return trigger('customProgress', [
      transition(':enter', [
        style({ width: '0%' }),
        animate(`${config.duration}ms ${config.easing || 'ease-in'}`, 
          style({ width: '100%' }))
      ])
    ]);
  }

  /**
   * Cria animação de typing effect
   */
  createTypingAnimation(config: AnimationConfig) {
    return trigger('customTyping', [
      transition(':enter', [
        style({ width: '0%' }),
        animate(`${config.duration}ms ${config.easing || 'ease-in'}`, 
          style({ width: '100%' }))
      ])
    ]);
  }
}
